import { catchError } from '@bigo/helpers';
import { useRoute, useRouter } from 'vue-router';
import authApi, { enrichAgentInfo } from '@/apis/auth';
import { STORAGE_KEY } from '@/constants/storage';
import { getAuthUrl } from '@/libs/auth';
import { confirm } from '@/libs/modal';
import { RouteName } from '@/router/names';
import { useAppStore } from '@/store/app';
import { isBigolive, getTokenFromNativiApi, gotoLoginPage } from '@/common/utils';
import { loadLang } from '@/i18n';
import { liveAgentUnionOssGetSelfInfo, liveAgentServiceGetPermissions } from '@/api';
import { updateLoggerConfig } from '@/common/utils/initialize';
import useTrack from './useTrack';
import { STATUS_CODE } from '@/constants';
import { useI18n } from 'vue-i18n';
import AuthCommunicator from '@/libs/AuthCommunicator';
import { track } from '@bigo/logger';
import getIsBanLogin from '@/compositions/useLoginban';
import { AccountPermission } from '@/constants/enum';
import { USER_ACTION } from '@/views/payment/ByBeansMethodView/constants/ByBeansMethodView';

const AUTH_CODE = {
  SUCCESS: 0,
  NOT_LOGIN: 700001,
};

const urlData = getAuthUrl();
const communicator = AuthCommunicator.getInstance(urlData.authPage);

let timer = null;
let loginModalOpened = false;

// 是否使用新的获取token方案
const useNewWay = true;

export default function useAuth() {
  const apiBaseUrl = urlData.api;
  const appStore = useAppStore();
  const route = useRoute();
  const router = useRouter();
  const { report } = useTrack();
  const { report: report66965 } = useTrack('66965_4');
  const { t } = useI18n();

  const logout = () => {
    if (useNewWay) {
      return communicator.logout();
    }
    return new Promise((resolve, reject) => {
      fetch(`${apiBaseUrl}/official_website_login/logout`, {
        credentials: 'include',
      })
        .then((response) => {
          // 清空多语言设置
          localStorage.setItem('lang', '');
          return response.json();
        })
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const handleExchangeToken = async () => {
    const deviceId = localStorage.getItem(STORAGE_KEY.DEVICE_ID);
    return new Promise((resolve, reject) => {
      fetch(`${apiBaseUrl}/official_website_login/exchangeToken?deviceId=${deviceId}`, {
        credentials: 'include',
      })
        .then((response) => {
          return response.json();
        })
        .then((res) => {
          const { code, data } = res || {};
          if (code === AUTH_CODE.SUCCESS) {
            report('ExchangeTokenSuccess');
            resolve(data.token);
          } else {
            if (code === AUTH_CODE.NOT_LOGIN) {
              appStore.logout();
              router.replace({ name: RouteName.Login });
            }
            report('ExchangeTokenError');
            reject(res);
          }
        })
        .catch((error) => {
          report('ExchangeTokenError');
          reject(error);
        });
    });
  };

  const getToken = async () => {
    if (process.env.NODE_ENV === 'development') {
      return appStore.token;
    }

    if (isBigolive) {
      try {
        const token = await getTokenFromNativiApi();
        appStore.setToken(token);
        return token;
      } catch (error) {
        gotoLoginPage();
        return;
      }
    }

    if (useNewWay) {
      const token = await communicator.getToken();
      if (token) {
        report('ExchangeTokenByIframeSuccess');
        appStore.setToken(token);
        return token;
      }
      report('ExchangeTokenByIframeError');
      appStore.logout();
      if (getIsBanLogin()) return;
      router.replace({ name: RouteName.Login });
    } else {
      try {
        const token = await handleExchangeToken();
        if (token) {
          appStore.setToken(token);
          return token;
        }
      } catch (error) {
        return null;
      }
    }

    return null;
  };

  const handleGetAgencyInfo = async () => {
    const { code, data } = await liveAgentUnionOssGetSelfInfo();
    if (code === STATUS_CODE.SUCCESS) {
      const { role, bigo_id, recruit_scout_permission } = data?.member_info || {};
      if (role) {
        appStore.setAgentRole(role);
      }
      if (bigo_id) {
        appStore.setUnModifyBigoId(bigo_id);
      }
      // 字段缺失时默认拒绝，避免经纪人越权新增推广员。
      appStore.setScoutRecruitPermission(recruit_scout_permission === true);
    }
  };

  const getUserInfo = async () => {
    const token = await getToken();
    if (token) {
      await catchError(appStore.getTransferPermission());
      // 81497 新增接口返回新权限，优先级比getUserInfo返回的要高
      const [permissionsErr, permissionData] = await catchError(
        liveAgentServiceGetPermissions({ permission_query_entry_type: 3 }),
      );
      const permissions = permissionData?.data?.permissions;
      const [err, userInfo] = await catchError(authApi.getUserInfo());
      if (!err && !permissionsErr) {
        if (userInfo.permissions && permissions) {
          userInfo.permissions = userInfo.permissions.concat(permissions);
          if (userInfo.permissions.includes(AccountPermission.AgencyPaymentMethod)) {
            // by beans method入口曝光
            let role = 0;
            if (appStore.isAgency) {
              role = 1;
            }
            if (appStore.isAgencySubAgent) {
              role = 2;
            }
            if (appStore.isAgencyManager) {
              role = 3;
            }
            report66965(USER_ACTION.byBeansMethodExpose, {
              role,
            });
          }
        }
        report('UserPermissions', userInfo?.permissions, {
          userType: appStore.userType,
        });
        appStore.setSelfBigo(userInfo?.bigoId);
        appStore.setBigoUid(userInfo?.bigoUid);
        return userInfo;
      }
    }
    return null;
  };

  const initUserInfo = async () => {
    const userInfo = await getUserInfo();
    // 如果是代理，获取运营后台的代理名称
    await enrichAgentInfo(userInfo);
    if (userInfo) {
      const userId = userInfo.bigoId;
      // 设置多语言
      let userLang = userInfo.language || navigator.language;
      // 后端的in语言码映射的是id
      if (userInfo.language && userInfo.language.toLowerCase() === 'in') {
        userLang = 'id';
      }
      if (localStorage.getItem('lang') !== userLang) {
        localStorage.setItem('lang', userLang);
        loadLang(userLang);
      }

      appStore.setLoginUser(userInfo);

      track({
        userId,
        uid: userId,
        log_type: 11,
        log_action: 'FETCH_USER_INFO_SUCCESS',
        log_content: JSON.stringify({ ...userInfo, token: '' }),
      });
      updateLoggerConfig({ userId });

      handleGetAgencyInfo();

      if (userInfo.permissions?.length) {
        const [, regions] = await catchError(authApi.getRegions());
        appStore.setRegions(regions);
      }
      return userInfo;
    }
    if (isBigolive) {
      gotoLoginPage();
    }

    return {};
  };

  const showUnauthorizedModal = async () => {
    const appStore = useAppStore();
    if (route.name === RouteName.Login || loginModalOpened || !appStore.loginUser) {
      return;
    }

    loginModalOpened = true;

    const user = await getUserInfo();
    if (user) {
      loginModalOpened = false;
      return;
    }

    const confirmed = await confirm({
      title: t('lang154'),
      content: t('lang155'),
      okText: t('lang156'),
      cancelText: t('lang157'),
    });

    loginModalOpened = false;
    if (!confirmed) return;

    if (isBigolive) {
      gotoLoginPage();
      return;
    }

    const redirect = window.encodeURIComponent(window.location.href);
    await router.replace({ name: RouteName.Login, query: { redirect } });
    appStore.logout();
  };

  // 五分钟一次
  const startTimer = () => {
    if (timer) {
      clearInterval(timer);
    }

    timer = setInterval(() => {
      initUserInfo();
    }, 5 * 60 * 1000);
  };

  const clearTimer = () => {
    if (timer) {
      clearInterval(timer);
    }
  };

  const init = () => {
    initUserInfo();
    startTimer();
  };

  return { logout, getToken, initUserInfo, showUnauthorizedModal, startTimer, clearTimer, init };
}
